import type { ITeamRepository } from "@/core/repositories/interfaces/ITeamRepository";
import type { UpdateTeamMemberInput } from "@/core/repositories/interfaces/ITeamRepository";
import type { CreateTeamMemberInput } from "@/core/repositories/interfaces/ITeamRepository";
import type { TeamMember } from "@/core/services/types/team.types";
import { createClient } from "@/lib/supabase";

export class TeamRepository implements ITeamRepository {
  async findAll(): Promise<TeamMember[]> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("team_members")
      .select("*")
      .order("last_name", { ascending: true })
      .order("first_name", { ascending: true });

    if (error) {
      console.error("[TeamRepository.findAll]", error.message);
      return [];
    }

    return data ?? [];
  }

  async create(input: CreateTeamMemberInput): Promise<TeamMember | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("team_members")
      .insert({
        first_name: input.first_name,
        last_name: input.last_name,
        email: input.email.trim().toLowerCase(),
        role: input.role,
        is_active: input.is_active,
        avatar_url: input.avatar_url,
      })
      .select("*")
      .maybeSingle();

    if (error) {
      console.error("[TeamRepository.create]", error.message);
      return null;
    }

    return data;
  }

  async delete(id: string): Promise<boolean> {
    const supabase = await createClient();

    const { error } = await supabase.from("team_members").delete().eq("id", id);

    if (error) {
      console.error("[TeamRepository.delete]", error.message);
      return false;
    }

    return true;
  }

  async update(
    id: string,
    input: UpdateTeamMemberInput,
  ): Promise<TeamMember | null> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("team_members")
      .update({
        first_name: input.first_name,
        last_name: input.last_name,
        email: input.email.trim().toLowerCase(),
        role: input.role,
        is_active: input.is_active,
        avatar_url: input.avatar_url,
      })
      .eq("id", id)
      .select("*")
      .maybeSingle();

    if (error) {
      console.error("[TeamRepository.update]", error.message);
      return null;
    }

    return data;
  }
}
